/**
 * Auth state management.
 *
 * On app start: call checkAuth() to verify session.
 * - Inside Telegram (initData present) → always authenticated.
 * - Browser → calls GET /api/auth/me; 401 = not authenticated.
 *
 * Stored in Zustand so any component can read it without a query.
 */

import { create } from 'zustand'
import { api } from '@/lib/api'
import { isInsideTelegram } from '@/lib/telegram'

interface AuthState {
  status: 'unknown' | 'authenticated' | 'unauthenticated'
  userId: number | null
  role: string
  setAuthenticated: (userId: number, role: string) => void
  setUnauthenticated: () => void
}

interface AuthMe {
  user_id: number
  role: string
  provider?: string
}

export const useAuthStore = create<AuthState>((set) => ({
  status: 'unknown',
  userId: null,
  role: '',
  setAuthenticated: (userId, role) => set({ status: 'authenticated', userId, role }),
  setUnauthenticated: () => set({ status: 'unauthenticated', userId: null, role: '' }),
}))

export function markLoggedOut(): void {
  useAuthStore.getState().setUnauthenticated()
}

export async function checkAuth(): Promise<boolean> {
  // Inside Telegram the initData header is always valid — skip the round-trip.
  if (isInsideTelegram()) {
    useAuthStore.getState().setAuthenticated(-1, '')
    return true
  }
  try {
    const data = await api.get<AuthMe>('/auth/me')
    useAuthStore.getState().setAuthenticated(data.user_id, data.role)
    return true
  } catch {
    try {
      const guestData = await api.post<{ user_id: number; role: string }>('/auth/guest', {})
      useAuthStore.getState().setAuthenticated(guestData.user_id, guestData.role)
      return true
    } catch {
      markLoggedOut()
      return false
    }
  }
}

export async function hasRealLogin(): Promise<boolean> {
  if (isInsideTelegram()) return true
  try {
    const data = await api.get<AuthMe>('/auth/me')
    return data.provider !== 'guest'
  } catch {
    return false
  }
}
